import { useServices } from "@/services/hooks/useServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateLabelDto, UpdateLabelDto } from "@/services/label.service";
import { LabelType } from "@/types/common";

// Get all labels with pagination
export const useLabels = (
  page: number = 1,
  limit: number = 10,
  labelType?: LabelType,
  enabled?: boolean
) => {
  const { labelService } = useServices();

  return useQuery({
    queryKey: ["labels", { page, limit, labelType }],
    queryFn: async () => {
      const response = await labelService.getLabels({
        page,
        limit,
        labelType: labelType || LabelType.MAILBOX,
      });
      return {
        data: response.data,
        totalPages: response.totalPages,
        total: response.total,
      };
    },
    refetchOnWindowFocus: false,
    enabled: enabled || true,
  });
};

// Get single label
export const useLabel = (id: string) => {
  const { labelService } = useServices();

  return useQuery({
    queryKey: ["labels", id],
    queryFn: () => labelService.getLabelById(id),
    enabled: !!id, // Only run query if id exists
  });
};

// Create label
export const useCreateLabel = () => {
  const { labelService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (labelData: CreateLabelDto) =>
      labelService.createLabel(labelData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
};

// Update label
export const useUpdateLabel = () => {
  const { labelService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      labelData,
    }: {
      id: string;
      labelData: UpdateLabelDto;
    }) => labelService.updateLabel(id, labelData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
};

// Delete label
export const useDeleteLabel = () => {
  const { labelService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => labelService.deleteLabel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
};

// Bulk delete labels
export const useBulkDeleteLabels = () => {
  const { labelService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => labelService.bulkDeleteLabels(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
};

// Bulk update labels
export const useBulkUpdateLabels = () => {
  const { labelService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { ids: string[]; updates: UpdateLabelDto }) =>
      labelService.bulkUpdateLabels(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
};
